/**
 * @file        src/pages/platform/TenantList.jsx
 * @description Tenant registry (super_admin only) — name, domain, status, and
 *              the two gated status actions. No delete action: suspending is
 *              the only "turn off" a tenant, matching the backend (no
 *              DELETE /api/tenants/:id).
 * @author      Kinetika Labs
 * @created     2026-07-27
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-07-27 Claude — Initial (multi-tenant super admin platform).
 *   - 2026-08-10 Claude — Go-live workflow: a new tenant is 'pending' until the
 *                          "Go Live" action here passes the readiness checklist
 *                          (GET /:id/readiness, re-run server-side on the PATCH
 *                          — 409 TENANT_NOT_READY otherwise). Suspend moved
 *                          here from the edit page (same confirm + password
 *                          gate); the edit page no longer has a Status field.
 *   - 2026-09-17 Claude — The suspend re-auth field is now <PasswordInput>.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import ReadinessChecklist, { useReadiness } from './ReadinessChecklist';
import PasswordInput from '../../components/PasswordInput';

const STATUS_TONE = { active: 'ok', pending: 'pend', suspended: 'rej' };

export default function TenantList() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [goLive, setGoLive] = useState(null);   // tenant row, or null
  const [suspend, setSuspend] = useState(null); // tenant row, or null

  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.tenants.list(params),
    queryFn: () => get('/tenants', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['tenants'] });
    setGoLive(null);
    setSuspend(null);
  };

  return (
    <>
      <PageHead
        title="Tenant List"
        subtitle="Platform · registered tenant databases"
        actions={<Link to="/platform/tenants/new" className="btn primary"><Icon name="plus" /> Add Tenant</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name / Domain</label>
            <input placeholder="Search tenants…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Domain</th><th>Database</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No tenants yet. Click “Add Tenant” to register one.</td></tr>
                ) : rows.map((t, idx) => (
                  <tr key={t.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{t.name}</b></td>
                    <td className="mono">{t.domain}</td>
                    <td className="mono"><small className="muted">{t.db_host}:{t.db_port}/{t.db_name}</small></td>
                    <td><Chip tone={STATUS_TONE[t.status] || 'rej'}>{t.status}</Chip></td>
                    <td className="row-act">
                      <Link to={`/platform/tenants/${t.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      {t.status !== 'active' ? (
                        <button className="btn primary sm" onClick={() => setGoLive(t)}>Go Live</button>
                      ) : (
                        <button className="btn ghost sm" onClick={() => setSuspend(t)}>Suspend</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {goLive ? <GoLiveModal tenant={goLive} onDone={refresh} onCancel={() => setGoLive(null)} /> : null}
      {suspend ? <SuspendModal tenant={suspend} onDone={refresh} onCancel={() => setSuspend(null)} /> : null}
    </>
  );
}

// --- Go Live -------------------------------------------------------------------
// Runs the readiness checklist and only then offers Activate. The server re-runs
// the same checks on PATCH status→active (409 TENANT_NOT_READY), so this dialog
// is a preview of the boundary, not the boundary itself.
function GoLiveModal({ tenant, onDone, onCancel }) {
  const { data, isLoading, isError, refetch, isFetching } = useReadiness(tenant.id);

  const mut = useMutation({
    mutationFn: () => patch(`/tenants/${tenant.id}`, { status: 'active' }),
    onSuccess: () => {
      toast.ok('Tenant live', `${tenant.name} is now active — its storefront and admin console are reachable.`);
      onDone();
    },
    onError: (err) => {
      const apiErr = err.response?.data?.error;
      toast.err('Could not go live', apiErr?.message || 'Activation failed');
      refetch(); // the server saw something this dialog didn't — re-show reality
    },
  });

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 560 }}>
        <div className="lk-modal-head">
          <h3>Go live: {tenant.name}</h3>
          <button className="ib" aria-label="Close" onClick={onCancel}><Icon name="x" /></button>
        </div>
        <div className="det-body">
          <p className="muted">
            Going live makes <b>{tenant.domain}</b> serve customers and take real payments.
            Every check must pass first.
          </p>
          <ReadinessChecklist tenantId={tenant.id} readiness={data} isLoading={isLoading} isError={isError} />
          <div className="form-actions" style={{ marginTop: 14 }}>
            <button type="button" className="btn ghost" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Re-checking…' : 'Re-run checks'}
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={!data?.ready || mut.isPending}
              title={data?.ready ? undefined : 'Complete every check first'}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? 'Activating…' : 'Activate tenant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Suspend -------------------------------------------------------------------
// Two steps, same shape as WalletActionModal: an explicit "are you sure" (Cancel
// here means nothing further happens — no password is ever asked), then, only on
// confirm, a password step the backend re-verifies (tenants.service.update)
// before actually flipping status. Suspending 404s the tenant's storefront and
// admin console on the very next request and stops its background jobs on the
// next cron tick, so it gets the same re-auth gate as moving wallet money.
function SuspendModal({ tenant, onDone, onCancel }) {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'password'
  const [password, setPassword] = useState('');
  const [pwErr, setPwErr] = useState('');

  const mut = useMutation({
    mutationFn: () => patch(`/tenants/${tenant.id}`, { status: 'suspended', password }),
    onSuccess: () => {
      toast.ok('Tenant suspended', `${tenant.name} is offline.`);
      onDone();
    },
    onError: (err) => {
      const apiErr = err.response?.data?.error;
      if (apiErr?.code === 'BAD_CREDENTIALS') return setPwErr('Incorrect password.');
      toast.err('Could not suspend', apiErr?.message || 'Failed');
    },
  });

  function submitPassword(e) {
    e.preventDefault();
    if (!password) return;
    setPwErr('');
    mut.mutate();
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 460 }}>
        <div className="lk-modal-head">
          <h3>{step === 'confirm' ? 'Suspend this tenant?' : 'Confirm with your password'}</h3>
          <button className="ib" aria-label="Close" onClick={onCancel}><Icon name="x" /></button>
        </div>

        {step === 'confirm' ? (
          <div className="det-body">
            <p className="muted">
              Suspending <b>{tenant.name}</b> takes its storefront and admin console offline
              immediately — every request to its domain 404s. Background jobs (wallet expiry, hold
              sweeps, reconciliation) stop running for it on the next cron tick. This is reversible
              via Go Live, which re-runs the readiness checks.
            </p>
            <div className="form-actions">
              <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
              <button type="button" className="btn primary" onClick={() => setStep('password')}>
                Yes, suspend
              </button>
            </div>
          </div>
        ) : (
          <form className="det-body" onSubmit={submitPassword}>
            <p className="muted">Suspending <b>{tenant.name}</b></p>
            <div className="field full">
              <label>Confirm your password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                             placeholder="Your admin password" autoComplete="new-password" data-lpignore="true" data-1p-ignore autoFocus />
              {pwErr ? <div className="field-error">{pwErr}</div> : null}
            </div>
            <div className="form-actions">
              <button type="button" className="btn ghost" onClick={() => setStep('confirm')} disabled={mut.isPending}>Back</button>
              <button type="submit" className="btn primary" disabled={mut.isPending || !password}>
                {mut.isPending ? 'Please wait…' : 'Suspend tenant'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
